import { z } from 'zod';
import { changePasswordFormSchema } from '../../auth/change-password-form.js';
import { permissionSchema } from '../../permissions.js';
/**
 * The current Account User, the page they land on, the website they last used
 * and the end of their session — the dashboard API side of Account User login
 * (`technical.md`, Dashboard API). Every route here is `@AnyAccountUser()`
 * (`patterns/auth-patterns.md`, 2).
 */
export const meResponseSchema = z.object({
    /** The Account User's id, not the login's. */
    id: z.uuid(),
    /** The login's name, trimmed, or `null` when it is blank. */
    name: z.string().nullable(),
    email: z.email(),
    isSuperUser: z.boolean(),
    /** Effective permissions, in `navPermissionOrder`. */
    permissions: z.array(permissionSchema),
});
export const getMe = {
    method: 'GET',
    path: '/api/dashboard/me',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: meResponseSchema,
};
export const updateMeBodySchema = z.object({
    lastWebsiteId: z.uuid(),
});
/** Records the last website used. `204`, and nothing to return. */
export const updateMe = {
    method: 'PATCH',
    path: '/api/dashboard/me',
    params: z.object({}),
    query: z.object({}),
    body: updateMeBodySchema,
    response: z.void(),
};
/**
 * Where a logged-in Account User lands (`patterns/ui-patterns.md`, 2,
 * "After log in"). `conversations`, `analytics`, `knowledge` and
 * `website_setup` carry a `websiteId`; the rest carry `null`.
 */
export const LANDING_DESTINATIONS = [
    'conversations',
    'analytics',
    'knowledge',
    'website_setup',
    'add_website',
    'users_and_permissions',
    'billing',
    'no_permissions',
    'no_websites',
];
export const landingDestinationSchema = z.enum(LANDING_DESTINATIONS);
export const landingResponseSchema = z.object({
    destination: landingDestinationSchema,
    websiteId: z.uuid().nullable(),
});
export const getLanding = {
    method: 'GET',
    path: '/api/dashboard/me/landing',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: landingResponseSchema,
};
/**
 * Log out. A dashboard route rather than Better Auth's own `/sign-out`, so it
 * answers in this project's error shape (`patterns/api-conventions.md`, 1).
 */
export const logOut = {
    method: 'DELETE',
    path: '/api/dashboard/me/session',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
/**
 * Changes the Account User's own password: the current password and a new one
 * that meets the password rule (`patterns/auth-patterns.md`, 1). A sub-resource
 * created with `POST`, because it is an action
 * (`patterns/api-conventions.md`, 3). `204` on success, with the new session's
 * cookie; every other session of the login ends.
 *
 * A dashboard route rather than Better Auth's own `/change-password`, so the
 * global guards and this project's error shape apply
 * (`patterns/api-conventions.md`, 1).
 */
export const changePassword = {
    method: 'POST',
    path: '/api/dashboard/me/password-changes',
    params: z.object({}),
    query: z.object({}),
    body: changePasswordFormSchema,
    response: z.void(),
};
//# sourceMappingURL=me.js.map